/**
 * The menu dialog's week model, extracted from cafeteria.js so it can be tested without a page.
 *
 * The dialog shows one week of five fields, but a school publishes the month — so an imported file
 * routinely covers four or five weeks at once. A draft holds every week the parent has in play, keyed by
 * its Monday, and the dialog paints one of them at a time. Everything here is pure: no DOM, no fetch.
 *
 * Dates are handled as ISO strings through UTC, never local time. A menu date is a calendar date, and
 * constructing `new Date("2026-09-07")` in a timezone behind UTC yields the 6th — which would shift a
 * whole week's dishes back a day for every parent west of Greenwich.
 */

export const DAYS_PER_WEEK = 5;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function parseIso(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function toIso(date) {
  return date.toISOString().slice(0, 10);
}

export function addDays(iso, days) {
  const date = parseIso(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return toIso(date);
}

export function shortDate(iso) {
  const date = parseIso(iso);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

export function weekLabel(weekStartIso) {
  const monday = parseIso(weekStartIso);
  const friday = parseIso(addDays(weekStartIso, 4));
  return monday.getUTCMonth() === friday.getUTCMonth()
    ? `${MONTHS[monday.getUTCMonth()]} ${monday.getUTCDate()} – ${friday.getUTCDate()}`
    : `${MONTHS[monday.getUTCMonth()]} ${monday.getUTCDate()} – ${MONTHS[friday.getUTCMonth()]} ${friday.getUTCDate()}`;
}

/**
 * Five empty Mon–Fri rows.
 *
 * `entrees` and `sides` are lists, one string per dish. They used to be three text fields — a main, a
 * comma-separated "other choices", and comma-separated sides — which meant a real menu's four to six
 * coequal mains arrived as one truncated string a parent could neither read nor edit. Each dish now
 * gets its own slot here and its own chip on screen.
 *
 * `allergens` is provider metadata (item name → tags) riding invisibly alongside — the form never edits
 * it, and the server re-filters it against the names that actually get saved, so a renamed dish sheds
 * tags nobody can vouch for.
 */
export function blankWeekRows() {
  const rows = [];
  for (let i = 0; i < DAYS_PER_WEEK; i++) rows.push({ entrees: [], sides: [], allergens: null, noSchool: false });
  return rows;
}

/** Normalizes anything the server or an older bundle hands us into the list shape. */
export function toRow(day) {
  if (!day) return { entrees: [], sides: [], allergens: null, noSchool: false };

  // Accept the legacy string shape too: a page cached before this shipped still posts entree/altEntree,
  // and a stored menu read back through an old prefill would otherwise render as an empty day.
  const entrees = Array.isArray(day.entrees)
    ? day.entrees.slice()
    : splitList(day.entree).concat(splitList(day.altEntree));
  const sides = Array.isArray(day.sides) ? day.sides.slice() : splitList(day.sides);

  return {
    entrees: entrees.map((e) => String(e).trim()).filter(Boolean),
    sides: sides.map((s) => String(s).trim()).filter(Boolean),
    allergens: day.itemAllergens || day.allergens || null,
    noSchool: !!day.noSchool
  };
}

function splitList(value) {
  return typeof value === "string" && value.length > 0 ? value.split(",") : [];
}

/** A marked closure counts as content: it is a statement about the day that has to reach the server. */
export function dayHasContent(row) {
  return row.entrees.length > 0 || row.sides.length > 0 || row.noSchool;
}

export function weekHasContent(rows) {
  return rows.some(dayHasContent);
}

export function filledDayCount(rows) {
  return rows.filter(dayHasContent).length;
}

/**
 * A fresh draft on the week the dialog opened. `target` is remembered separately from `active` because
 * the two diverge the moment an import lands on a different month, and only the target is written
 * unconditionally on save.
 */
export function createDraft(weekStartIso, rows, targetHasStoredMenu = false) {
  return {
    weeks: { [weekStartIso]: rows || blankWeekRows() },
    order: [weekStartIso],
    active: weekStartIso,
    target: weekStartIso,
    // Whether the target week had a saved menu when the dialog opened. This is what separates "the
    // parent is clearing a menu" (must write the empty week) from "the dialog happened to open on an
    // empty week" (must not) — see weeksToSave.
    targetHasStoredMenu,
    importedWeeks: [],
    imported: false,
    // Weeks the user has typed into. Needed by the non-primary meal's save rule: a breakfast week the
    // user emptied must still be written (that is how it is cleared), while one they never touched must
    // not be — an unconditional target write would wipe a stored breakfast menu just for opening lunch.
    dirty: []
  };
}

/** Marks a week as user-edited. Content and dirtiness differ: an emptied week has no content but intent. */
export function markDirty(draft, weekStartIso) {
  if (!draft.dirty) draft.dirty = [];
  if (!draft.dirty.includes(weekStartIso)) draft.dirty.push(weekStartIso);
}

/**
 * Merges imported weeks into a draft, in place, and returns how many days were filled.
 *
 * Imported days overwrite; a day the file did not cover keeps whatever is already in the draft, so a
 * parent who typed Monday before uploading does not lose it. A day whose date is not one of the five
 * school days of the week it arrived under is ignored rather than written to the nearest slot — putting
 * a dish under the wrong date is the one outcome worse than leaving the day blank.
 */
export function applyImportedWeeks(draft, weeks) {
  // Nothing arrived, so nothing was imported. Marking the draft imported anyway freezes it against
  // re-prefilling on a school change — which is how a lunch-only fetch left one school's breakfast
  // menu sitting in the draft, ready to be saved under a different school.
  if (!weeks || weeks.length === 0) return 0;

  let dayCount = 0;

  weeks.forEach((week) => {
    if (!draft.weeks[week.weekStartIso]) {
      draft.weeks[week.weekStartIso] = blankWeekRows();
      draft.order.push(week.weekStartIso);
    }
    const rows = draft.weeks[week.weekStartIso];

    (week.days || []).forEach((day) => {
      for (let index = 0; index < DAYS_PER_WEEK; index++) {
        if (addDays(week.weekStartIso, index) !== day.dateIso) continue;
        rows[index] = toRow(day);
        dayCount++;
      }
    });
  });

  // ISO dates sort lexicographically, so the strip reads left to right in calendar order.
  draft.order.sort();
  draft.importedWeeks = weeks.map((w) => w.weekStartIso);
  draft.imported = true;
  // Land the parent on the first week the file filled, which is rarely the one they opened.
  if (weeks.length > 0) draft.active = weeks[0].weekStartIso;

  return dayCount;
}

/**
 * Which weeks a save writes.
 *
 * For the primary meal (lunch), the opened week always goes, even when blank — that is how a parent
 * clears a menu entered by mistake. For a secondary meal, the target is written only when it holds
 * content or the user edited it: the dialog always opens on lunch, and writing an untouched breakfast
 * target would store an empty breakfast week — or erase a saved one — as a side effect of saving lunch.
 * Imported weeks in either meal go only when they hold something.
 */
export function weeksToSave(draft, alwaysIncludeTarget = true) {
  const dirty = draft.dirty || [];
  return draft.order.filter((iso) => {
    if (weekHasContent(draft.weeks[iso]) || dirty.includes(iso)) return true;
    if (iso !== draft.target || !alwaysIncludeTarget) return false;
    // An empty target is only worth writing when it is clearing something. Writing it unconditionally
    // is what made an import of one week announce "Save 2 weeks" — the second being the empty week the
    // dialog happened to open on.
    return !!draft.targetHasStoredMenu;
  });
}

/**
 * The weeks the strip should offer. After an import that is the weeks the FILE produced, plus the week
 * the dialog opened on only when it holds something — listing an untouched target under "weeks found in
 * that file" told a parent their one-week PDF contained two weeks, one of them empty.
 */
export function weeksToShow(draft) {
  const imported = draft.importedWeeks || [];
  return draft.order.filter((iso) =>
    imported.includes(iso)
    || weekHasContent(draft.weeks[iso])
    || (draft.dirty || []).includes(iso)
    || (iso === draft.target && !!draft.targetHasStoredMenu));
}

/** Groups an import's weeks by meal; days without one are lunch (pre-meals server responses). */
export function splitWeeksByMeal(weeks) {
  const lunch = [];
  const breakfast = [];
  (weeks || []).forEach((w) => (w.meal === "breakfast" ? breakfast : lunch).push(w));
  return { lunch, breakfast };
}

/**
 * One week's rows as the menu-week API's day payload.
 *
 * `entrees` and `sides` go out as lists, one element per dish, exactly as the draft holds them. They
 * were once three text fields joined and split around a comma, which is how a real menu's four to six
 * coequal mains arrived as one unreadable string and how "Pizza Dippers" ended up filed as a side.
 */
export function weekPayload(weekStartIso, rows) {
  return rows.map((row, index) =>
    row.noSchool
      // A closure sends the flag alone; anything still listed on the day is the parent's undo buffer,
      // not a menu — the server would drop it anyway.
      ? { date: addDays(weekStartIso, index), noSchool: true, entrees: [], sides: [] }
      : {
          date: addDays(weekStartIso, index),
          entrees: row.entrees.slice(),
          sides: row.sides.slice(),
          // Round-tripped, not authored: the server keeps tags only for names that still exist.
          itemAllergens: row.allergens || undefined
        });
}

/**
 * What to tell the parent when an import fails. The server's own message wins whenever there is one —
 * it knows whether the file was unreadable, the budget spent, or the provider down, and each of those
 * has a different next step.
 */
export function importErrorMessage(status, json) {
  if (json && json.error) return json.error;
  if (status === 413) return "That file is too large. Try a photo of just the menu page.";
  if (status === 404) return "Menu import isn't available right now.";
  return "We couldn't read that file. Try another, or enter the week by hand.";
}
